<template>
    <app-dialog v-if="user" v-model="isActive" :persistent="saving" :size="editing || creating ? 'x-large' : 'large'">
        <template #title>
            <div style="width: 100%" class="mb-4">
                <div v-if="creating || editing">
                    <div>{{ creating ? $t('create') : $t('edit') }} {{ $t('user') }}</div>
                    <v-layout class="mt-4">
                        <field-string
                            ref="input.title"
                            v-model="user.title"
                            :disabled="saving"
                            :label="$t('title')"
                            hide-details
                            class="mr-4"
                            style="width: 6rem"
                        />
                        <field-string
                            ref="input.name"
                            v-model="user.name"
                            :disabled="saving"
                            :label="$t('name')"
                            required
                            hide-details
                            style="width: 100%"
                        />
                    </v-layout>
                </div>
                <div v-else>
                    <v-layout align-center justify-space-between>
                        <div>
                            <h3>{{ user.title ? user.title : null }} {{ user.name }}</h3>
                            <p class="body-1 mb-0">{{ user.email }}</p>
                        </div>
                        <app-chip
                            :prefix="$t('status')"
                            :text="$t(`enums.user_status.${user.status}`)"
                            :success="user.status === UserStatusEnum.ACTIVE"
                            :error="user.status === UserStatusEnum.SUSPENDED"
                            :warning="user.status === UserStatusEnum.UNVERIFIED"
                            :grey="user.status === UserStatusEnum.INACTIVE"
                        />
                    </v-layout>
                </div>
            </div>
        </template>

        <template #before>
            <v-progress-linear :indeterminate="saving || deleting" :height="4" color="primaryLight" value="100" />
        </template>

        <template #default>
            <transition appear name="fade-transition" mode="out-in">
                <v-card-text v-if="editing || creating" class="px-6 pt-4">
                    <field-toggle
                        v-if="creating && !isCompany() && !isProvider()"
                        ref="input.can_login"
                        v-model="user.can_login"
                        :disabled="saving"
                        :label="$t('can_login')"
                        :info="$t('if_this_is_left_unticked_the_user_will_be_created_without_sending_any_emails')"
                    />
                    <field-user-status
                        v-if="editing"
                        ref="input.status"
                        v-model="user.status"
                        :disabled="saving || user.status === UserStatusEnum.UNVERIFIED"
                        :exclude="user.status === UserStatusEnum.UNVERIFIED ? [] : [UserStatusEnum.UNVERIFIED]"
                        :label="$t('status')"
                    />
                    <field-string
                        ref="input.email"
                        v-model="user.email"
                        :disabled="saving"
                        :label="$t('email')"
                        required
                    />
                    <field-string
                        ref="input.job_title"
                        v-model="user.job_title"
                        :disabled="saving"
                        :label="$t('job_title')"
                    />
                    <field-string
                        ref="input.fca_number"
                        v-model="user.fca_number"
                        :disabled="saving"
                        :label="$t('fca_number')"
                    />
                    <field-phone-numbers
                        ref="input.phone_numbers"
                        v-model="user.phone_numbers"
                        :disabled="saving"
                        :label="$t('phone_numbers')"
                        required
                    />
                    <field-user-roles
                        ref="input.roles"
                        v-model="user.roles"
                        :disabled="saving"
                        :label="$t('user_roles')"
                        required
                        :exclude-organisation-types="excludedOrganisationTypes"
                    />
                </v-card-text>
                <v-card-text v-else>
                    <div class="px-2 mb-4">
                        <label class="d-block text--small primaryText--text">{{ $t('job_title') }}</label>
                        <p class="body-1 mb-0">{{ user.job_title || $t('none') }}</p>
                    </div>
                    <div class="px-2 mb-4">
                        <label class="d-block text--small primaryText--text">{{ $t('fca_number') }}</label>
                        <p class="body-1 mb-0">{{ user.fca_number || $t('none') }}</p>
                    </div>
                    <div class="px-2 mb-4">
                        <label class="d-block text--small primaryText--text">{{ $t('phone_numbers') }}</label>
                        <p v-if="!user.phone_numbers || !user.phone_numbers.length" class="body-1 mb-0">
                            {{ $t('none') }}
                        </p>
                        <template v-else>
                            <p v-for="(phoneNumber, index) in user.phone_numbers" :key="index" class="body-1 mb-0">
                                {{ phoneNumber.computed_name }}
                            </p>
                        </template>
                    </div>
                    <div class="px-2 mb-4">
                        <label class="d-block text--small primaryText--text">{{ $t('user_roles') }}</label>
                        <p v-if="!user.roles || !user.roles.length" class="body-1 mb-0">
                            {{ $t('none') }}
                        </p>
                        <template v-else>
                            <p v-for="(role, index) in user.roles" :key="index" class="body-1 mb-0">
                                {{ role.computed_name }}
                            </p>
                        </template>
                    </div>
                </v-card-text>
            </transition>
        </template>

        <template #actions>
            <app-button
                v-if="editing && isGrowthInvest()"
                color="error"
                secondary
                :text="$t('delete')"
                class="mr-2"
                :disabled="saving"
                :loading="deleting"
                :icon-left="getIcon('delete', 'fad')"
                @click="deleteUser"
            />

            <v-spacer />

            <app-button secondary :text="$t('cancel')" class="mr-2" :disabled="saving" @click="isActive = false" />

            <app-button
                v-if="!editing && !creating && isSuperuser()"
                :text="$t('edit')"
                :icon="getIcon('edit', 'fad')"
                @click="editing = true"
            />

            <app-button
                v-if="(editing || creating) && isSuperuser()"
                :text="$t('save')"
                :icon="getIcon('save', 'fas')"
                :loading="saving"
                class="ml-2"
                @click="createUser"
            />
        </template>
    </app-dialog>
</template>

<script>
import snack from '@/lib/helpers/snack';
import dialog from '@/lib/helpers/dialog';
import validate from '@/mixins/validate';
import { User } from '@/models';
import UserStatusEnum from '@/enums/user/status';
import OrganisationTypeEnum from '@/enums/organisation/type';

export default {
    name: 'UserModal',
    components: {
        AppDialog: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppDialog'
            ),
        FieldUserStatus: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/fields/UserStatus'
            ),
        FieldPhoneNumbers: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/fields/PhoneNumbers'
            ),
        FieldUserRoles: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/fields/UserRoles'
            )
    },
    mixins: [validate],
    props: {
        value: {
            type: Boolean,
            required: true
        },
        userId: {
            type: String,
            required: true
        },
        presetAttr: {
            type: Object,
            default: () => {}
        }
    },
    data: () => ({
        user: null,
        saving: false,
        deleting: false,
        editing: false,
        creating: false,
        UserStatusEnum,
        OrganisationTypeEnum
    }),
    computed: {
        isActive: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        excludedOrganisationTypes() {
            let included = [];

            if (this.isAdviser() && !this.isGrowthInvest()) {
                included = [OrganisationTypeEnum.ADVISER, OrganisationTypeEnum.INVESTOR];
            } else if (this.isProvider()) {
                included = [OrganisationTypeEnum.PRODUCT_PROVIDER];
            } else if (this.isCompany()) {
                included = [OrganisationTypeEnum.COMPANY];
            }

            if (included.length === 0) {
                return [];
            }

            return Object.values(OrganisationTypeEnum).filter(type => !included.includes(type));
        }
    },
    watch: {
        userId: {
            immediate: true,
            handler(id) {
                this.setUser(id);
            }
        }
    },
    methods: {
        async setUser(id) {
            if (id === 'create') {
                this.creating = true;
                this.user = new User(this.presetAttr);

                if (this.isCompany() || this.isProvider()) {
                    this.user.can_login = true;
                }
            } else {
                this.creating = false;
                this.user = await User.$get(id);
                this.$emit('input:user', this.user);
            }
        },
        createUser() {
            if (!this.isSuperuser()) {
                return;
            }

            if (!this.validate()) {
                return;
            }

            let hasAdviserRole = false;

            if (this.user && this.user.roles && this.user.roles.length) {
                hasAdviserRole = this.user.roles.some(role => role.roles.includes('ADVISER'));
            }

            if (hasAdviserRole && !this.user.fca_number) {
                dialog({
                    title: this.$t('fca_number_missing'),
                    text: this.$t('financial_services_register_reference_numbers_are_required'),
                    continueButtonText: this.$t('add') + ' ' + this.$t('fca_number'),
                    cancelButtonText: this.$t('continue'),
                    onCancelFn: this.saveUser
                });
            } else {
                this.saveUser();
            }
        },
        async saveUser() {
            if (!this.isSuperuser()) {
                return;
            }

            this.saving = true;

            try {
                let user = this.user;

                if (this.creating) {
                    user = await this.user.$create();
                } else {
                    await this.user.$update();
                }

                User.delete(user.id);
                await this.setUser(user.id);
                this.editing = false;

                snack({
                    timeout: 20000,
                    type: 'success',
                    text: this.$t('user_updated')
                });
            } catch (error) {
                this.handleError(error, this.saveUser);
            }

            this.saving = false;
        },
        async deleteUser() {
            if (!this.isGrowthInvest()) {
                return;
            }

            const removeItem = async () => {
                this.deleting = true;
                try {
                    await User.api.remove(this.user.id);
                    this.$emit('remove:user', this.user);
                } catch (error) {
                    this.handleError(error);
                }
                this.deleting = false;
            };

            dialog({
                cancelButtonText: this.$t('cancel'),
                continueButtonColor: 'error',
                title: this.$t('are_you_sure'),
                text: this.$t('proceeding_will_permanently_delete') + ' ' + this.user.name + '.',
                onContinueFn: removeItem
            });
        }
    }
};
</script>
